import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import type { WorkflowStatus } from '@/api/workflows';
import { statusColor } from '@/originator/statusUtils';

type Props = {
  status: WorkflowStatus;
  /**
   * For Approved rows: when false, the tag displays "Archived" instead of "Published".
   * Templates pass true (the workflow's overall status); version rows pass their own
   * IsActive flag so an Approved-but-superseded version renders as Archived.
   * Leave undefined for non-Approved statuses (it's ignored there).
   */
  isActive?: boolean;
  /**
   * Render decision-verb labels ("Approved" / "Rejected") instead of the
   * workflow-state labels ("Published" / "Rejected"). Used by Recent Decisions
   * where the column describes WHAT the approver did, not the workflow's
   * current state. Default false (workflow-state semantics).
   */
  decisionMode?: boolean;
};

/**
 * Single source of truth for status pills across the app — Workflows card, Version
 * History table, Workflow Details, Recent Decisions. Encodes the one branching rule
 * the design has ("Approved + not active = Archived") so it can't disagree between
 * pages.
 */
export function StatusTag({ status, isActive, decisionMode }: Props) {
  const { t } = useTranslation();
  // Recent Decisions semantics — match the approver dashboard tab labels.
  // Approved + active → "Published" (Published Workflows tab). Approved + not
  // active → "Approved" (Approved Workflows tab). Rejected → "Rejected". Used
  // in place of the Archived label so the recent-decisions row mirrors the
  // tab the workflow currently sits in.
  if (decisionMode) {
    if (status === 'Approved' && isActive === true) {
      return <Tag color={statusColor(status)}>{t('workflow.statusLabels.Approved')}</Tag>;
    }
    if (status === 'Approved' || status === 'Rejected') {
      return <Tag color={statusColor(status)}>{t(`workflow.decisionLabels.${status}`)}</Tag>;
    }
  }
  if (status === 'Approved' && isActive === false) {
    return <Tag color="default">{t('workflow.versions.statusArchived')}</Tag>;
  }
  return <Tag color={statusColor(status)}>{t(`workflow.statusLabels.${status}`)}</Tag>;
}
